"use client";

import { usePostHog } from "@posthog/react";
import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { parseError } from "evlog";
import { CheckIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Field, FieldDescription, FieldError, FieldLabel } from "#/components/ui/field";
import { Input } from "#/components/ui/input";
import { Spinner } from "#/components/ui/spinner";
import { updateDisplayNameInputSchema } from "#/lib/playerSettingsSchemas";
import { patchMyPlayerProfileQuery } from "#/lib/queries";
import { updateMyDisplayName } from "#/server/updateMyDisplayName";

import type { ProfileUserId } from "./-profile.types";

const displayNameInputId = "profile-display-name";
const saveDebounceMs = 500;
const savedFeedbackMs = 2000;

const passwordManagerIgnoreInputProps = {
  autoComplete: "off",
  "data-1p-ignore": true,
  "data-bwignore": true,
  "data-form-type": "other",
  "data-lpignore": "true",
} as const;

function useSavedCheckmark() {
  const [showSaved, setShowSaved] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const markSaved = useCallback(() => {
    setShowSaved(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShowSaved(false);
    }, savedFeedbackMs);
  }, []);

  const clearSaved = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowSaved(false);
  }, []);

  return { showSaved, markSaved, clearSaved };
}

function DisplayNameInputSaveIndicator({
  isSaving,
  showSaved,
}: {
  isSaving: boolean;
  showSaved: boolean;
}) {
  return (
    <div
      className="pointer-events-none absolute inset-y-0 right-2.5 flex w-4 items-center justify-center"
      aria-live="polite"
    >
      {isSaving ? <Spinner className="text-muted-foreground" /> : null}
      {!isSaving && showSaved ? (
        <CheckIcon className="size-4 text-muted-foreground" aria-label="Saved" />
      ) : null}
    </div>
  );
}

function useDisplayNameForm({
  userId,
  initialDisplayName,
}: {
  userId: ProfileUserId;
  initialDisplayName: string;
}) {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const savedValueRef = useRef(initialDisplayName.trim());
  const { showSaved, markSaved, clearSaved } = useSavedCheckmark();
  const [saveError, setSaveError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: { displayName: initialDisplayName },
    onSubmit: async ({ value }) => {
      const trimmed = value.displayName.trim();
      if (trimmed === savedValueRef.current) return;

      try {
        await updateMyDisplayName({ data: { displayName: trimmed } });
        savedValueRef.current = trimmed;
        setSaveError(null);
        markSaved();
        patchMyPlayerProfileQuery({ queryClient, userId, patch: { displayName: trimmed } });
      } catch (err) {
        const parsed = parseError(err);
        if (parsed.status >= 500) {
          posthog.captureException(err);
        }
        setSaveError(parsed.message);
      }
    },
  });

  const clearSaveError = useCallback(() => setSaveError(null), []);

  const submitIfValid = useCallback(() => {
    if (form.state.isValid) {
      void form.handleSubmit();
    }
  }, [form]);

  return {
    form,
    showSaved,
    clearSaved,
    saveError,
    clearSaveError,
    submitIfValid,
  };
}

type DisplayNameFieldApi = {
  name: string;
  state: {
    value: string;
    meta: {
      isTouched: boolean;
      isValid: boolean;
      errors: ReadonlyArray<unknown>;
    };
  };
  handleBlur: () => void;
  handleChange: (value: string) => void;
};

function DisplayNameInput({
  field,
  isInvalid,
  isSaving,
  showSaved,
  onChange,
}: {
  field: DisplayNameFieldApi;
  isInvalid: boolean;
  isSaving: boolean;
  showSaved: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <Input
        id={displayNameInputId}
        name={field.name}
        type="text"
        className="pr-9"
        {...passwordManagerIgnoreInputProps}
        value={field.state.value}
        onBlur={field.handleBlur}
        onChange={(e) => onChange(e.target.value)}
        placeholder=""
        aria-invalid={isInvalid}
      />
      <DisplayNameInputSaveIndicator isSaving={isSaving} showSaved={showSaved} />
    </div>
  );
}

function DisplayNameFieldContent({
  field,
  isSaving,
  showSaved,
  onChange,
  saveError,
}: {
  field: DisplayNameFieldApi;
  isSaving: boolean;
  showSaved: boolean;
  onChange: (value: string) => void;
  saveError: string | null;
}) {
  const validationErrors = field.state.meta.errors as Array<{ message?: string } | undefined>;
  const hasValidationError = field.state.meta.isTouched && !field.state.meta.isValid;
  const isInvalid = hasValidationError || saveError !== null;

  return (
    <Field data-invalid={isInvalid}>
      <FieldLabel htmlFor={displayNameInputId}>Display name</FieldLabel>
      <FieldDescription>How you want to appear on leaderboards.</FieldDescription>
      <DisplayNameInput
        field={field}
        isInvalid={isInvalid}
        isSaving={isSaving}
        showSaved={showSaved}
        onChange={onChange}
      />
      {hasValidationError ? (
        <FieldError errors={validationErrors} />
      ) : saveError !== null ? (
        <FieldError>{saveError}</FieldError>
      ) : null}
    </Field>
  );
}

export function DisplayNameField({
  userId,
  initialDisplayName,
}: {
  userId: ProfileUserId;
  initialDisplayName: string;
}) {
  const { form, showSaved, clearSaved, saveError, clearSaveError, submitIfValid } =
    useDisplayNameForm({
      userId,
      initialDisplayName,
    });

  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(isSubmitting) => (
        <form.Field
          name="displayName"
          validators={{
            onChange: updateDisplayNameInputSchema.shape.displayName,
            onBlur: updateDisplayNameInputSchema.shape.displayName,
          }}
          listeners={{
            onChangeDebounceMs: saveDebounceMs,
            onChange: submitIfValid,
            onBlur: submitIfValid,
          }}
        >
          {(field) => (
            <DisplayNameFieldContent
              field={field}
              isSaving={isSubmitting}
              showSaved={showSaved}
              saveError={saveError}
              onChange={(value) => {
                clearSaved();
                clearSaveError();
                field.handleChange(value);
              }}
            />
          )}
        </form.Field>
      )}
    </form.Subscribe>
  );
}
